import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { Link } from "gatsby"

export const buttonStyle = css`
  --button-border-color: var(
    --${props => (props.$primary ? "primary" : "secondary")}
  );
  --button-background: var(
    --${props => (props.$primary ? "primary" : "secondary")}
  );

  text-decoration: none;
  text-shadow: none;
  display: inline-flex;
  justify-content: center;
  flex-direction: row;
  gap: 20px;
  align-items: center;
  text-align: center;
  line-height: 1;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: var(--button-border-radius, 69px);
  border: 2px solid var(--button-border-color);
  cursor: pointer;
  transition: filter 0.3s ease-in-out, transform 0.1s ease-in-out;
  padding: var(--button-padding, 0.5em 2em 0.6em);
  box-sizing: border-box;
  color: var(--button-text-color, #fff);

  background: var(--button-background);
  width: ${props => (props.$fill ? "100%" : null)};

  ${props => {
    if (props.$small) {
      return css`
        font-size: 14px;
        min-width: none;
      `
    } else {
      return css`
        min-width: var(--button-min-width, 185px);
      `
    }
  }}

  ${props => {
    if (props.$inversed) {
      return css`
        color: var(--button-background);
        border: 2px solid var(--button-text-color, #fff);
        background-color: var(--button-text-color, #fff);
      `
    }
  }}

  ${props =>
    props.$plain &&
    css`
      padding: 0;
      background-color: transparent;
      border: 0;
      color: currentColor;
      box-shadow: none;
    `}

  &:hover {
    filter: saturate(1.3);
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.8;
    cursor: default;
  }
`

const StyledLink = styled(Link)`
  ${buttonStyle}
`

const StyledAnchor = styled.a`
  ${buttonStyle}
`

const StyledButton = styled.button`
  ${buttonStyle}
`

const IconWrapper = styled.div`
  margin-top: -1px;
`

const Button = props => {
  let icon = null
  let button = null
  if (props.icon) {
    icon = <IconWrapper>{props.icon}</IconWrapper>
  }

  button = (
    <StyledButton
      onClick={props.onClick}
      $inversed={props.inversed}
      $small={props.small}
      $fill={props.fill}
      $primary={props.primary}
      $plain={props.plain}
      style={props.style}
    >
      {props.label}
      {icon}
    </StyledButton>
  )

  if (props.submit) {
    button = (
      <StyledButton
        type="submit"
        $fill={props.fill}
        style={props.style}
        $primary={props.primary}
        $plain={props.plain}
        direction={props.direction}
        disabled={props.disabled}
        $small={props.small}
      >
        {props.label}
        {icon}
      </StyledButton>
    )
  }

  if (props.to) {
    button = (
      <StyledLink
        to={props.to}
        $inversed={props.inversed}
        $small={props.small}
        $fill={props.fill}
        $primary={props.primary}
        $plain={props.plain}
        style={props.style}
      >
        {props.label}
        {icon}
      </StyledLink>
    )
  }

  if (props.href) {
    button = (
      <StyledAnchor
        href={props.href}
        $inversed={props.inversed}
        $small={props.small}
        $fill={props.fill}
        $primary={props.primary}
        $plain={props.plain}
        style={props.style}
        target={props.external ? "_blank" : null}
        rel={props.external ? "noreferrer" : null}
      >
        {props.label}
        {icon}
      </StyledAnchor>
    )
  }

  return button
}

Button.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  to: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.element,
  inversed: PropTypes.bool,
  external: PropTypes.bool,
  plain: PropTypes.bool,
  small: PropTypes.bool,
  fill: PropTypes.bool,
  submit: PropTypes.bool,
  primary: PropTypes.bool,
  disabled: PropTypes.bool,
}

Button.defaultProps = {
  fill: false,
  inversed: false,
  submit: false,
  primary: false,
  plain: false,
  disabled: false,
  external: false,
}

export { Button }
